import {
  Modal,
  ProgressIndicator,
  ProgressStep,
} from 'carbon-components-react';
import React, { useState, useEffect } from 'react';
import Step1 from '../../Molecules/Steps/Step1';
import Step2 from '../../Molecules/Steps/Step2';
import Step3 from '../../Molecules/Steps/Step3';
import Step4 from '../../Molecules/Steps/Step4';
import {
  QuesValProps,
  que2ValueProps,
  que3ValueProps,
} from '../../MaximoJourneyAdvisor';
import { useIntl } from 'react-intl';
import messages from 'src/apps/maximo-journey-advisor/locales/messages';
import { segmentsTracking } from 'src/common/lib/tracker';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import data from '../../../data/results.json';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';

type PopUpModalProps = {
  langCode: string;
  bpName: string;
  logo: string;
  companyId: string;
  open: boolean;
  handleCloseModal: () => void;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  currentQuestion: string;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<string>>;
  isRequired: boolean;
  setIsRequired: React.Dispatch<React.SetStateAction<boolean>>;
  que1Value: string;
  setQue1Value: React.Dispatch<React.SetStateAction<string>>;
  que2Value: que2ValueProps;
  setQue2Value: React.Dispatch<React.SetStateAction<que2ValueProps>>;
  que3Value: que3ValueProps;
  setQue3Value: React.Dispatch<React.SetStateAction<que3ValueProps>>;
  trackQuesVal: QuesValProps;
  setTrackQuesVal: React.Dispatch<React.SetStateAction<QuesValProps>>;
  formComplete: boolean;
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setModel2: React.Dispatch<React.SetStateAction<boolean>>;
  setFormComplete: React.Dispatch<React.SetStateAction<boolean>>;
  handleUrxForm: () => void;
  model2: boolean;
  localeCode: string;
  reset: () => void;
  currency: string;
  country: string;
  cards: cardProps[];
  setCards: React.Dispatch<React.SetStateAction<cardProps[]>>;
  q1Img: string;
  setQ1Img: React.Dispatch<React.SetStateAction<string>>;
};

export type cardProps = {
  linkText: string;
  linkUrl: string;
  description: string;
};

export const getValueIfOnlyOneChecked = (options: any) => {
  let count = 0;
  let value = null;
  for (const key in options) {
    if (options.hasOwnProperty(key) && options[key].checked) {
      count++;
      value = options[key].value;
    }
  }
  return count === 1 ? value : count > 1 ? 'multi' : '';
};

const Assessment = ({
  open,
  handleCloseModal,
  step,
  que1Value,
  setQue1Value,
  que2Value,
  setQue2Value,
  setStep,
  isRequired,
  setIsRequired,
  que3Value,
  setQue3Value,
  trackQuesVal,
  setTrackQuesVal,
  bpName,
  logo,
  handleUrxForm,
  cards,
  setCards,
  q1Img,
  setQ1Img,
}: PopUpModalProps) => {
  const [resultProgress, setResultProgress] = useState(true);

  const [invalidQ1, setInvalidQ1] = useState(false);
  const [invalidQ2, setInvalidQ2] = useState(false);
  const [invalidQ3, setInvalidQ3] = useState(false);
  const isTab = useSmallBreakpoint('1024px');
  const isMob = useSmallBreakpoint('420px');

  function constructArray(answers: QuesValProps) {
    // Find the matching data object
    const matchingData = data.find(
      (item) =>
        item.Q1 === answers.Q1 &&
        item.Q2 === answers.Q2 &&
        item.Q3 === answers.Q3,
    );

    // If matching data found, construct the array of objects
    if (matchingData) {
      const result = [
        {
          linkText: matchingData.link1Text,
          linkUrl: matchingData.link1Url,
          description: matchingData.description1,
        },
        {
          linkText: matchingData.link2Text,
          linkUrl: matchingData.link2Url,
          description: matchingData.description2,
        },
        {
          linkText: matchingData.link3Text,
          linkUrl: matchingData.link3Url,
          description: matchingData.description3,
        },
        {
          linkText: matchingData.link4Text,
          linkUrl: matchingData.link4Url,
          description: matchingData.description4,
        },
      ];

      return result;
    } else {
      // Handle case where no matching data found
      return [];
    }
  }

  useEffect(() => {
    if (trackQuesVal.Q1 && trackQuesVal.Q2 && trackQuesVal.Q3) {
      const resultArray = constructArray(trackQuesVal);
      setCards(resultArray);
      setResultProgress(false);
    } else {
      setResultProgress(true);
    }
  }, [trackQuesVal]);

  const handleSegmentTracking = (cta: string) => {
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Maximo Journey Advisor Assessment',
      object: 'Maximo Journey Advisor Page',
      resultValue: 'CTA clicked',
      CTA: cta,
      location: 'Assessment',
      action: '',
      field: '',
    });
  };

  const intl = useIntl();

  const toggleInvalidPB = (
    answer: string,
    setInvalid: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (answer === '') {
      setInvalid(true);
    } else {
      setInvalid(false);
    }
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 1:
        setInvalidQ1(false);
        break;
      case 2:
        setInvalidQ2(false);
        break;
      case 3:
        setInvalidQ3(false);
        break;
    }
  };

  const q2Val = getValueIfOnlyOneChecked(que2Value.options);
  const q3Val = getValueIfOnlyOneChecked(que3Value.options);

  useEffect(() => {
    setTrackQuesVal({
      ...trackQuesVal,
      Q2: q2Val,
      Q3: q3Val,
    });
  }, [que1Value, q2Val, q3Val]);

  return (
    <div className="bx--grid">
      <Modal
        open={open}
        onRequestClose={handleCloseModal}
        passiveModal={false}
        primaryButtonText={
          step < 4
            ? intl.formatMessage(messages.next_btn)
            : intl.formatMessage(messages.demo_btn_text)
        }
        secondaryButtonText={
          step > 1
            ? intl.formatMessage(messages.back_btn)
            : intl.formatMessage(messages.close_btn)
        }
        preventCloseOnClickOutside={true}
        primaryButtonDisabled={isRequired}
        onRequestSubmit={() => {
          validateStep(step);
          if (step === 1 || step <= 3) {
            setStep((step) => step + 1);
          }
          if (!que1Value || !q2Val || !q3Val) {
            setIsRequired(true);
          } else if (step === 4) {
            handleUrxForm();
          }
        }}
        size="lg"
        onSecondarySubmit={() => {
          if (step === 6) {
            handleSegmentTracking('Back');
            setStep((step) => step - 1);
            const style = window.document.getElementById(
              `${'urx-form-container'}`,
            ) as HTMLDivElement;
            style.style.display = 'none';
          } else {
            if (step > 1) {
              setStep((step) => step - 1);
              handleSegmentTracking('Back');
              setIsRequired(false);
            } else {
              handleCloseModal();
              handleSegmentTracking('Close');
              setStep(1);
            }
          }
        }}
      >
        {!isTab && !isMob && (
          <div className="bx--row">
            <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4 border-pos">
              <div className="border-bottom margin10" />
            </div>
          </div>
        )}
        <div className="bx--row">
          <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
            {bpName ? (
              <BusinessPartner bpName={bpName} logo={logo} urxForm={true} />
            ) : null}
          </div>
        </div>
        <div className="bx--row margin10">
          <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-4 prog-mar-pad bkg-color">
            <ProgressIndicator
              spaceEqually={true}
              currentIndex={step === 4 ? 5 : step - 1}
              onChange={(e) => {
                toggleInvalidPB(que1Value, setInvalidQ1);
                toggleInvalidPB(q2Val, setInvalidQ2);
                toggleInvalidPB(q3Val, setInvalidQ3);
                setStep(e + 1);
              }}
              vertical={isMob ? true : !isTab}
            >
              <ProgressStep
                label={intl.formatMessage(messages.progress_step1_text)}
                secondaryLabel={que1Value}
                invalid={invalidQ1}
              />
              <ProgressStep
                invalid={invalidQ2}
                label={intl.formatMessage(messages.progress_step2_text)}
                secondaryLabel={
                  getValueIfOnlyOneChecked(que2Value.options) === 'multi'
                    ? intl.formatMessage(messages.multi_resp_text)
                    : getValueIfOnlyOneChecked(que2Value.options)
                }
                disabled={!trackQuesVal.Q1}
              />
              <ProgressStep
                invalid={invalidQ3}
                label={intl.formatMessage(messages.progress_step3_text)}
                secondaryLabel={
                  getValueIfOnlyOneChecked(que3Value.options) === 'multi'
                    ? intl.formatMessage(messages.multi_resp_text)
                    : getValueIfOnlyOneChecked(que3Value.options)
                }
                disabled={!trackQuesVal.Q2}
              />
              <ProgressStep
                invalid={false}
                label={intl.formatMessage(messages.progress_step4_text)}
                disabled={resultProgress}
              />
            </ProgressIndicator>
          </div>
          <div
            className={`bx--col-lg-12 bx--col-md-8 bx--col-sm-4 vl que-detail-bkgColor ${
              isTab && !isMob && 'tab-mar'
            }`}
          >
            {step === 1 && (
              <Step1
                que1Value={que1Value}
                setQue1Value={setQue1Value}
                setIsRequired={setIsRequired}
                setTrackQuesVal={setTrackQuesVal}
                trackQuesVal={trackQuesVal}
                q1Img={q1Img}
                setQ1Img={setQ1Img}
              />
            )}
            {step === 2 && (
              <Step2
                que2Value={que2Value}
                setQue2Value={setQue2Value}
                setIsRequired={setIsRequired}
                checkboxState={getValueIfOnlyOneChecked(que2Value.options)}
              />
            )}
            {step === 3 && (
              <Step3
                que3Value={que3Value}
                setQue3Value={setQue3Value}
                setIsRequired={setIsRequired}
                checkboxState={getValueIfOnlyOneChecked(que3Value.options)}
              />
            )}
            {step === 4 && (
              <Step4
                cardInfo1={cards[0]}
                cardInfo2={cards[1]}
                cardInfo3={cards[2]}
                cardInfo4={cards[3]}
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Assessment;
